
import { Routes, Route, useLocation } from "react-router-dom";
import Navbar from "./components/Navbar";
import { SupportBubble } from "./components/SupportBubble";
import ScrollToTop from "./components/ScrollToTop";
import { SubdomainRouter } from "./components/organizations/SubdomainRouter";
import { CompleteWhiteLabelBranding } from "./components/organizations/CompleteWhiteLabelBranding";
import { AuthGuard } from "./components/auth/AuthGuard";
import { TrialProvider } from "./contexts/TrialContext";
import { PersonaProvider } from "./components/personas/PersonaProvider";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import Index from "./pages/Index";
import Auth from "./pages/Auth";
import Dashboard from "./pages/Dashboard";
import Documents from "./pages/Documents";
import DocumentDetail from "./pages/DocumentDetail";
import Chat from "./pages/Chat";
import Settings from "./pages/Settings";
import Assessment from "./pages/Assessment";
import Welcome from "./pages/Welcome";
import PaymentSuccess from "./pages/PaymentSuccess";
import Referrals from "./pages/Referrals";
import AdminReferrals from "./pages/AdminReferrals";
import OrganizationSignup from "./pages/OrganizationSignup";
import OrganizationJoin from "./pages/OrganizationJoin";
import { OrganizationDashboard } from "./components/organizations/OrganizationDashboard";
import TeamOnboarding from "./pages/TeamOnboarding";
import EnterpriseOnboarding from "./pages/EnterpriseOnboarding";
import TeamMemberSignup from "./pages/TeamMemberSignup";
import PlanSelection from "./pages/PlanSelection";
import InvitationAccept from "./pages/InvitationAccept";

export const AppRoutes = () => {
  const location = useLocation();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  
  // Define routes where navbar should NOT be shown
  const noNavbarRoutes = [
    '/dashboard',
    '/documents',
    '/chat',
    '/settings',
    '/assessment',
    '/welcome',
    '/payment/success',
    '/referrals',
    '/admin/referrals',
    '/organization',
    '/team/onboarding',
    '/enterprise/onboarding',
    '/plan-selection',
    '/plans',
    '/pricing'
  ];
  
  // Define authenticated routes where support bubble should show
  const authenticatedRoutes = [
    '/dashboard',
    '/documents',
    '/chat',
    '/settings',
    '/assessment',
    '/welcome',
    '/payment/success',
    '/referrals',
    '/admin/referrals',
    '/organization',
    '/team/onboarding',
    '/enterprise/onboarding',
    '/plan-selection',
    '/plans',
    '/pricing'
  ];
  
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        setIsAuthenticated(!!session);
      } catch (error) {
        console.error('Auth check error:', error);
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setIsAuthenticated(!!session);
    });

    return () => subscription.unsubscribe();
  }, []);
  
  // Check if current route should show navbar
  const shouldShowNavbar = !noNavbarRoutes.some(route => 
    location.pathname.startsWith(route)
  );

  // Check if current route is authenticated AND user is actually logged in
  const shouldShowSupportBubble = !isLoading && isAuthenticated && authenticatedRoutes.some(route => 
    location.pathname.startsWith(route)
  );

  // Show scroll to top on public pages when user is not authenticated
  const shouldShowScrollToTop = !isAuthenticated && !authenticatedRoutes.some(route => 
    location.pathname.startsWith(route)
  );

  return (
    <TrialProvider>
      <PersonaProvider>
        <SubdomainRouter>
          <CompleteWhiteLabelBranding />
          
          {shouldShowNavbar && <Navbar />}
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/plan-selection" element={<PlanSelection />} />
            
            {/* Add routes for plans and pricing that redirect to plan-selection */}
            <Route path="/plans" element={<PlanSelection />} />
            <Route path="/pricing" element={<PlanSelection />} />
            
            {/* Protected routes requiring authentication */}
            <Route path="/dashboard" element={
              <AuthGuard requireSubscription={true}>
                <Dashboard />
              </AuthGuard>
            } />
            
            <Route path="/documents" element={
              <AuthGuard requireSubscription={true}>
                <Documents />
              </AuthGuard>
            } />
            
            <Route path="/documents/:id" element={
              <AuthGuard requireSubscription={true}>
                <DocumentDetail />
              </AuthGuard>
            } />
            
            <Route path="/chat" element={
              <AuthGuard requireSubscription={true}>
                <Chat />
              </AuthGuard>
            } />
            
            <Route path="/settings" element={
              <AuthGuard>
                <Settings />
              </AuthGuard>
            } />
            
            <Route path="/assessment" element={
              <AuthGuard>
                <Assessment />
              </AuthGuard>
            } />
            
            <Route path="/welcome" element={
              <AuthGuard>
                <Welcome />
              </AuthGuard>
            } />
            
            <Route path="/payment/success" element={
              <AuthGuard>
                <PaymentSuccess />
              </AuthGuard>
            } />
            
            <Route path="/referrals" element={
              <AuthGuard>
                <Referrals />
              </AuthGuard>
            } />
            
            <Route path="/admin/referrals" element={
              <AuthGuard>
                <AdminReferrals />
              </AuthGuard>
            } />
            
            <Route path="/organization/signup" element={
              <AuthGuard>
                <OrganizationSignup />
              </AuthGuard>
            } />
            
            <Route path="/organization/join/:token" element={
              <AuthGuard>
                <OrganizationJoin />
              </AuthGuard>
            } />
            
            <Route path="/organization/dashboard" element={
              <AuthGuard>
                <OrganizationDashboard />
              </AuthGuard>
            } />
            
            <Route path="/organization" element={
              <AuthGuard>
                <OrganizationDashboard />
              </AuthGuard>
            } />
            
            <Route path="/team/onboarding" element={
              <AuthGuard>
                <TeamOnboarding />
              </AuthGuard>
            } />
            
            <Route path="/enterprise/onboarding" element={
              <AuthGuard>
                <EnterpriseOnboarding />
              </AuthGuard>
            } />
            
            <Route path="/team/signup/:token" element={
              <TeamMemberSignup />
            } />
            
            <Route path="/invitation/accept" element={
              <InvitationAccept />
            } />
          </Routes>
          
          {shouldShowSupportBubble && <SupportBubble />}
          {shouldShowScrollToTop && <ScrollToTop />}
        </SubdomainRouter>
      </PersonaProvider>
    </TrialProvider>
  );
};

export default AppRoutes;
